import type {ComponentType} from "react";

import {LogOutIcon, ServerIcon, ShieldIcon, UserIcon} from "lucide-react";
import {Link, NavLink, Outlet, useNavigate} from "react-router-dom";

import {useAuth} from "@/lib/auth";

import {Button} from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";

import {cn} from "@/lib/utils";

interface NavItem {
  to: string;
  label: string;
  icon?: ComponentType<{className?: string}>;
}

const customerNav: NavItem[] = [
  {to: "/servers", label: "Servers", icon: ServerIcon},
  {to: "/plans", label: "Plans"},
];

const adminNav: NavItem[] = [
  {to: "/admin", label: "Dashboard"},
  {to: "/admin/lairs", label: "Lairs"},
  {to: "/admin/plans", label: "Plans"},
  {to: "/admin/os-images", label: "OS Images"},
];

/**
 * Top-bar layout shared by all authenticated pages. Picks the nav set based
 * on the current principal scope (customer vs admin) and renders the matched
 * route via <Outlet/>.
 */
export const Layout = () => {
  const {scope, principal, logout} = useAuth();
  const navigate = useNavigate();

  const isAdmin = scope === "account";
  const items = isAdmin ? adminNav : customerNav;

  const displayName =
    (isAdmin ? principal?.account?.display_name : principal?.user?.display_name) ??
    (isAdmin ? principal?.account?.email : principal?.user?.email) ??
    "Account";

  const initials = displayName
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("") || "?";

  const handleLogout = async () => {
    await logout();
    navigate(isAdmin ? "/admin/login" : "/login", {replace: true});
  };

  return (
    <div className="min-h-screen bg-background">
      <header className="sticky top-0 z-40 border-b bg-background/95 backdrop-blur">
        <div className="mx-auto flex h-14 max-w-7xl items-center gap-6 px-4">
          <Link
            to={isAdmin ? "/admin" : "/servers"}
            className="flex items-center gap-2 font-semibold"
          >
            {isAdmin ? <ShieldIcon className="size-5 text-primary" /> : <ServerIcon className="size-5 text-primary" />}
            <span>Eyrie{isAdmin ? " Admin" : ""}</span>
          </Link>

          <nav className="flex flex-1 items-center gap-1">
            {items.map((item) => (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/admin"}
                className={({isActive}) =>
                  cn(
                    "rounded-md px-3 py-1.5 text-sm font-medium transition-colors",
                    isActive
                      ? "bg-muted text-foreground"
                      : "text-muted-foreground hover:bg-muted hover:text-foreground",
                  )
                }
              >
                {item.label}
              </NavLink>
            ))}
          </nav>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="gap-2">
                <span className="flex size-6 items-center justify-center rounded-full bg-primary text-xs font-semibold text-primary-foreground">
                  {initials}
                </span>
                <span className="hidden max-w-32 truncate sm:inline">{displayName}</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-48">
              <DropdownMenuLabel>
                <div className="flex flex-col">
                  <span className="text-sm font-medium">{displayName}</span>
                  <span className="text-xs font-normal text-muted-foreground">
                    {isAdmin ? "Platform admin" : "Customer"}
                  </span>
                </div>
              </DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem disabled>
                <UserIcon /> Profile
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem variant="destructive" onSelect={handleLogout}>
                <LogOutIcon /> Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <main className="mx-auto max-w-7xl p-4 md:p-6">
        <Outlet />
      </main>
    </div>
  );
};
